import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";
import Events from "@/pages/Events";
import Gallery from "@/pages/Gallery";
import Social from "@/pages/Social";

// CULTURE — the living expression of CrewZNatioN.
//
// This is a PARENT experience that composes three previously top-level
// sections — Events, Gallery, Social — as three visually distinct
// experiences under one narrative arc:
//   WE GATHER → WE CAPTURE → WE CONNECT.
//
// Each sub-experience is rendered by importing its existing page
// component with `embedded={true}` — no content is rewritten, no data
// is re-fetched twice, and the individual pages retain their own
// visual identities (Events on ivory, Gallery on the institutional
// dark ground, Social on the institutional dark ground with its live
// Meta Graph feed). Culture supplies the outer chapter frame, the
// transition strip, and the internal section navigator.
//
// Legacy `/events`, `/gallery`, `/social` and Arabic mirrors redirect
// to `/culture#events`, `/culture#gallery`, `/culture#social` so old
// links continue to resolve to the correct anchored section.

const COPY = {
    en: {
        overline: "Culture",
        title: (
            <>
                Culture is the living expression of <Brand />.
            </>
        ),
        lead: (
            <>
                The machines brought us together. The people gave it shape. What we do together — as
                a community, on the road, in the archive, across our feeds — is our culture.
            </>
        ),
        arc: [
            { key: "gather", label: "We Gather", meta: "Events" },
            { key: "capture", label: "We Capture", meta: "Gallery" },
            { key: "connect", label: "We Connect", meta: "Social" },
        ],
        navHeading: "In this section",
        navLinks: [
            { href: "#events", label: "Events" },
            { href: "#gallery", label: "Gallery" },
            { href: "#social", label: "Social" },
        ],
        metaTitle: "Culture — CrewZNatioN",
        metaDesc:
            "Culture is the living expression of CrewZNatioN — the events we gather at, the images we capture, and the community we stay connected to.",
        canonical: "https://www.crewznation.com/culture",
    },
    ar: {
        overline: "ثقافتنا",
        title: (
            <>
                ثقافتنا هي التعبير الحي عن <Brand />.
            </>
        ),
        lead: (
            <>
                جمعتنا الآلات، وصاغنا الناس. وما نصنعه معًا — مجتمعًا على الطريق، وأرشيفًا للصورة،
                وحضورًا على منصاتنا — هو ثقافتنا.
            </>
        ),
        arc: [
            { key: "gather", label: "نجتمع", meta: "الفعاليات" },
            { key: "capture", label: "نوثّق", meta: "المعرض" },
            { key: "connect", label: "نتواصل", meta: "سوشل" },
        ],
        navHeading: "في هذا القسم",
        navLinks: [
            { href: "#events", label: "الفعاليات" },
            { href: "#gallery", label: "المعرض" },
            { href: "#social", label: "سوشل" },
        ],
        metaTitle: "ثقافتنا — CrewZNatioN",
        metaDesc:
            "ثقافتنا هي التعبير الحي عن CrewZNatioN — الفعاليات التي نجتمع فيها، والصور التي نوثّقها، والمجتمع الذي نبقى على تواصلٍ معه.",
        canonical: "https://www.crewznation.com/ar/culture",
    },
};

export default function Culture({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    const t = isAr ? COPY.ar : COPY.en;
    const { hash } = useLocation();

    // Head-tag manager (title + description + canonical) — matches the
    // convention established for Privacy / Account Deletion / Contact.
    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;

        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    // Anchor scroll after mount / hash change. Waits one frame so the
    // three embedded chapters have laid out before we jump to the id.
    useEffect(() => {
        if (!hash) return;
        const id = hash.slice(1);
        const timer = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
        return () => clearTimeout(timer);
    }, [hash]);

    return (
        <div
            dir={isAr ? "rtl" : "ltr"}
            className={`min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}
            data-testid={isAr ? "culture-page-ar" : "culture-page"}
        >
            <Nav />

            {/* PARENT HERO — deliberate departure from the Landing hero.
                Text-forward on the institutional dark ground, no image
                overlay. Culture speaks in its own voice before the
                sub-experiences begin. */}
            <section
                className="relative px-6 md:px-12 lg:px-20 pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24 max-w-6xl mx-auto"
                data-testid="culture-hero"
            >
                <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p
                        className={`overline overline-primary ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                    >
                        {t.overline}
                    </p>
                </div>
                <h1
                    className={`${isAr ? "font-arabic text-4xl md:text-5xl lg:text-6xl !leading-[1.25]" : "font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.015em]"} text-[var(--crewz-text)] max-w-4xl`}
                    data-testid="culture-title"
                >
                    {t.title}
                </h1>
                <p
                    className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-lg md:text-xl leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                    data-testid="culture-lead"
                >
                    {t.lead}
                </p>
            </section>

            {/* STORY-ARCHITECTURE STRIP — WE GATHER → WE CAPTURE → WE CONNECT.
                Renders as three columns on lg+ with a subtle gold connector
                between them (chevron arrow rendered per direction). Stacks
                vertically on smaller viewports with a vertical connector.
                No CTAs here — the strip is a promise of what follows. */}
            <section
                aria-label={isAr ? "الرحلة" : "The arc"}
                className="px-6 md:px-12 lg:px-20 pb-16 md:pb-24 max-w-6xl mx-auto"
                data-testid="culture-arc"
            >
                <div className="border-t border-white/10 pt-10 md:pt-14 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-10 relative">
                    {t.arc.map((step, i) => (
                        <div
                            key={step.key}
                            className={`relative ${isAr ? "text-right" : "text-left"}`}
                            data-testid={`culture-arc-step-${step.key}`}
                        >
                            <p
                                className={`overline mb-3 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""} text-zinc-500`}
                            >
                                {String(i + 1).padStart(2, "0")} · {step.meta}
                            </p>
                            <p
                                className={`${isAr ? "font-arabic text-3xl md:text-4xl !leading-[1.3]" : "font-display text-3xl md:text-4xl leading-[1.05]"} text-[var(--crewz-text)]`}
                            >
                                {step.label}
                            </p>
                            {/* Connector — arrow that points to the next
                                step. On md+ it sits to the trailing edge
                                of the column; on smaller widths the strip
                                stacks and the connector is omitted for
                                calm mobile composition. */}
                            {i < t.arc.length - 1 && (
                                <span
                                    aria-hidden="true"
                                    className={`hidden md:block absolute top-1/2 -translate-y-1/2 text-[var(--crewz-gold)]/60 text-2xl select-none ${isAr ? "-left-6" : "-right-6"}`}
                                >
                                    {isAr ? "←" : "→"}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* INTERNAL SECTION NAVIGATOR — sticky on lg+ so the reader
                can always jump between the three chapters below without
                scrolling back to the top. Restrained typography, single
                thin rule underneath, and gold on hover/active-hash.
                Anchor-only (does not change history entries beyond a
                fragment). */}
            <nav
                aria-label={isAr ? "التنقّل داخل ثقافتنا" : "In-page navigator"}
                className="sticky top-24 z-30 bg-[var(--crewz-bg)]/85 backdrop-blur-md border-y border-white/10"
                data-testid="culture-section-nav"
            >
                <div
                    className={`px-6 md:px-12 lg:px-20 max-w-6xl mx-auto py-4 flex flex-wrap items-center gap-x-6 gap-y-2 ${isAr ? "justify-end" : ""}`}
                >
                    <p
                        className={`overline text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}
                    >
                        {t.navHeading}
                    </p>
                    {t.navLinks.map((l) => {
                        const active = hash === l.href;
                        return (
                            <a
                                key={l.href}
                                href={l.href}
                                className={`text-[14px] tracking-[0.22em] uppercase font-bold transition-colors duration-300 ${
                                    active ? "text-[var(--crewz-gold)]" : "text-zinc-300 hover:text-white"
                                } ${isAr ? "font-arabic normal-case tracking-normal !text-[15px]" : ""}`}
                                data-testid={`culture-nav-${l.href.slice(1)}`}
                            >
                                {l.label}
                            </a>
                        );
                    })}
                </div>
            </nav>

            {/* CHAPTER I — WE GATHER · EVENTS
                Embedded Events page (ivory ground preserved). The
                embedded flag skips its own Nav/Footer so it flows as a
                chapter inside Culture. */}
            <div data-testid="culture-chapter-events" className="border-t border-white/10">
                <Events locale={locale} embedded />
            </div>

            {/* CHAPTER II — WE CAPTURE · GALLERY
                Embedded Gallery page (institutional dark ground). */}
            <div data-testid="culture-chapter-gallery" className="border-t border-white/10">
                <Gallery locale={locale} embedded />
            </div>

            {/* CHAPTER III — WE CONNECT · SOCIAL
                Embedded Social page (institutional dark ground + live
                Meta Graph feed). */}
            <div data-testid="culture-chapter-social" className="border-t border-white/10">
                <Social locale={locale} embedded />
            </div>

            <PageFooter />
        </div>
    );
}
